/* import __COLOCATED_TEMPLATE__ from './new-notify-slack-channel-overlay.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
// It feels better to disable this lint rule and stay consistent with all the other popovers in rules
/* eslint-disable @intercom/intercom/no-component-inheritance */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import BaseFilterBlockPopover from 'predicates/components/filter-block/base-filter-block-popover';

export default class NewSettings extends BaseFilterBlockPopover {
  @service appService: any;
  @service intl: any;

  @tracked channels: any[] = [];
  @tracked selectedChannelId = '';
  @tracked error: string | null = null;
  @tracked searchText = '';

  // Type assertion for inherited properties
  componentAttrs: any;
  hideDropdownOverlay!: () => void;

  // Initialize the task when the component is created
  _initTask = taskFor(this.fetchSlackChannels).perform();

  get appId() {
    return this.appService.app.id;
  }

  get isLoading() {
    return taskFor(this.fetchSlackChannels).isRunning;
  }

  get notificationChannels() {
    return this.channels.filter((channel) => channel.channel_purpose === 'notification_only');
  }

  get filteredChannels() {
    let channels = this.notificationChannels;

    if (this.searchText.trim()) {
      let searchTerm = this.searchText.toLowerCase();
      channels = channels.filter(
        (channel: any) =>
          channel.channel_name.toLowerCase().includes(searchTerm) ||
          channel.slack_team_name.toLowerCase().includes(searchTerm),
      );
    }

    return channels;
  }

  get groupedChannels() {
    let groupsMap = new Map();

    this.filteredChannels.forEach((channel) => {
      let teamName = channel.slack_team_name;
      if (!groupsMap.has(teamName)) {
        groupsMap.set(teamName, []);
      }
      groupsMap.get(teamName)?.push(channel);
    });

    return Array.from(groupsMap.entries()).map(([teamName, teamChannels]) => ({
      heading: teamName,
      items: teamChannels.map((channel: any) => ({
        text: `#${channel.channel_name}`,
        value: channel.id,
      })),
    }));
  }

  get allChannelValues() {
    return this.notificationChannels.map((channel) => channel.id);
  }

  get allChannels() {
    return this.notificationChannels.map((channel) => ({
      text: `${channel.channel_name} (${channel.slack_team_name})`,
      value: channel.id,
    }));
  }

  get selectedChannelDisplay() {
    if (!this.selectedChannelId) {
      return '';
    }

    let selectedChannel = this.notificationChannels.find(
      (channel) => channel.id === this.selectedChannelId,
    );

    return selectedChannel ? `#${selectedChannel.channel_name}` : '';
  }

  get safeSelectedValue() {
    if (this.channels.length === 0 || !this.selectedChannelId) {
      return undefined;
    }

    return this.allChannelValues.includes(this.selectedChannelId)
      ? this.selectedChannelId
      : undefined;
  }

  @task({ drop: true })
  *fetchSlackChannels(): any {
    try {
      this.error = null;
      let params = {
        app_id: this.appId,
        'includes[]': 'slack_integration',
      };
      let data: any = yield get('/ember/slack/v2/channels', params);
      this.channels = data;
      this.initializeSelectedChannel();
    } catch (err: any) {
      this.error =
        'operator.workflows.visual-builder.notify-slack-channel.new-settings.generic-error';
    }
  }

  initializeSelectedChannel(): void {
    if (this.channels.length === 0) {
      return;
    }

    let actionData = this.componentAttrs?.action?.actionData;

    if (actionData?.intercom_slack_channel_id) {
      let channel = this.findChannelById(actionData.intercom_slack_channel_id);
      if (channel) {
        this.selectedChannelId = channel.id;
        return;
      }
    }

    if (actionData?.slack_channel_id) {
      let channel = this.findChannelBySlackId(actionData.slack_channel_id);
      if (channel) {
        this.selectedChannelId = channel.id;
        this.updateActionData({
          ...actionData,
          intercom_slack_channel_id: channel.id,
        });
        return;
      }
    }
    this.selectedChannelId = '';
  }

  findChannelById(channelId: string): any {
    return this.channels.find((channel: any) => channel.id === channelId);
  }

  findChannelBySlackId(slackChannelId: string): any {
    return this.channels.find((channel: any) => channel.channel_id === slackChannelId);
  }

  updateActionData(actionData: any): void {
    if (this.componentAttrs?.action) {
      this.componentAttrs.action.actionData = actionData;
    }
  }

  @action
  updateSearchText(event: Event): void {
    let target = event.target as HTMLInputElement;
    this.searchText = target.value;
  }

  @action
  handleChannelChange(intercomChannelId: string): void {
    this.selectedChannelId = intercomChannelId;
    let selectedChannel = this.findChannelById(intercomChannelId);
    if (selectedChannel) {
      if (this.componentAttrs?.action?.actionData) {
        let newActionData = {
          ...this.componentAttrs.action.actionData,
          slack_channel_id: selectedChannel.channel_id,
          intercom_slack_channel_id: selectedChannel.id,
          slack_channel_name: selectedChannel.channel_name,
        };
        this.updateActionData(newActionData);
      }

      // Call onChange to notify parent component
      if (this.componentAttrs?.onChange) {
        this.componentAttrs.onChange();
      }

      // Close the dropdown after selection
      this.hideDropdownOverlay();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Rules::NewNotifySlackChannelOverlay': typeof NewSettings;
    'rules/new-notify-slack-channel-overlay': typeof NewSettings;
  }
}
